import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import SortableTh from '../../components/common/SortableTh';
import React, { useEffect, useState } from 'react';
import { useTableSort } from '../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppFilterBar, AppFilterInput, AppFilterSelect } from '../../components/filters';
import klubService from '../../services/klubService';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import { AppButton, AppFormLabel, AppSpinner, AppTextarea } from '../../components/ui';
import '../../styles/kkp-planning-page.scss';
const TABLE_SORT_COLUMNS = {
  entreprise: { type: 'string', getValue: (row) => row.entreprise },
  contact: { type: 'string', getValue: (row) => row.contact_nom },
  telephones: { type: 'string', getValue: (row) => row.telephones },
  statut: { type: 'string', getValue: (row) => (row.avis_donne ? 'oui' : 'non') },
  dateAppel: { type: 'date', getValue: (row) => row.date_appel },
};

const KlubAvisPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(items, TABLE_SORT_COLUMNS);

  const load = () => {
    setLoading(true);
    const params = { search, limit: 200 };
    const single = filter.length === 1 ? filter[0] : '';
    if (single === 'sans') params.sans_avis = true;
    else if (single === 'oui') params.avis_donne = 'true';
    else if (single === 'non') params.avis_donne = 'false';

    klubService.listAvis(params)
      .then((res) => {
        let rows = res.avis ?? [];
        if (filter.length > 1) {
          rows = rows.filter((avis) => filter.some((f) => {
            if (f === 'oui') return Boolean(avis.avis_donne);
            if (f === 'non') return !avis.avis_donne;
            if (f === 'sans') return !avis.avis_donne && !(avis.texte ?? '').trim();
            return false;
          }));
        }
        setItems(rows);
      })
      .catch((err) => notify.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpand = (avis) => {
    if (expandedId === avis.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(avis.id);
    setEditText(avis.texte ?? '');
  };

  const handleSave = async (avis) => {
    setSaving(true);
    try {
      await klubService.updateAvis(avis.id, {
        texte: editText,
        avis_donne: editText.trim().length > 0,
      });
      setExpandedId(null);
      notify.success('Avis enregistré');
      load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Avis Klub"
        actions={(
          <AppButton
            as={Link}
            to={ROUTES.klub.dashboard}
            size="sm"
            variant="outline-secondary"
            icon="mdi:arrow-left"
          >
            Retour
          </AppButton>
        )}
      />

      <AppFilterBar
        onReset={() => { setSearch(''); setFilter([]); }}
        resetDisabled={!search && filter.length === 0}
      >
        <AppFilterInput
          id="avis-search"
          label="Recherche"
          col="col-12 col-md-6"
          placeholder="Entreprise, contact…"
          value={search}
          onChange={setSearch}
        />
        <AppFilterSelect
          id="avis-statut"
          label="Statut avis"
          col="col-12 col-md-4"
          value={filter}
          onChange={setFilter}
          multiple={false}
          options={[
            { value: 'oui', label: 'Avis donné' },
            { value: 'non', label: 'Non donné' },
            { value: 'sans', label: 'À collecter (sans avis)' },
          ]}
        />
      </AppFilterBar>

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : items.length === 0 ? (
            <div className="text-center py-5 text-secondary-light">Aucun avis</div>
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="entreprise" label="Entreprise" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="contact" label="Contact" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="telephones" label="Téléphones" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="dateAppel" label="Date appel" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh label="" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((avis) => (
                    <React.Fragment key={avis.id}>
                      <tr>
                        <td className="fw-medium">{avis.entreprise}</td>
                        <td>{avis.contact_nom ?? '—'}</td>
                        <td>{avis.telephones ?? '—'}</td>
                        <td>
                          <span className={`badge ${avis.avis_donne ? 'bg-success-focus text-success-main' : 'bg-warning-focus text-warning-main'}`}>
                            {avis.avis_donne ? 'Oui' : 'Non'}
                          </span>
                        </td>
                        <td>{avis.date_appel ?? '—'}</td>
                        <td className="d-flex gap-1">
                          <AppButton size="sm" variant="outline-primary" onClick={() => handleExpand(avis)}>
                            {expandedId === avis.id ? 'Fermer' : 'Éditer'}
                          </AppButton>
                          {avis.adherent_id && (
                            <AppButton as={Link} to={ROUTES.klub.adherentDetail(avis.adherent_id)} size="sm" variant="outline-secondary">
                              Adhérent
                            </AppButton>
                          )}
                        </td>
                      </tr>
                      {expandedId === avis.id && (
                        <tr>
                          <td colSpan={6} className="bg-neutral-50">
                            <AppFormLabel className="form-label text-sm">Témoignage</AppFormLabel>
                            <AppTextarea
                              className="mb-2"
                              rows={4}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                            />
                            {avis.notes_internes && (
                              <p className="text-secondary-light text-sm mb-2">Notes : {avis.notes_internes}</p>
                            )}
                            <AppButton size="sm" variant="success" loading={saving} onClick={() => handleSave(avis)}>
                              Enregistrer
                            </AppButton>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default KlubAvisPage;
